// @ts-strict-ignore
import {
  getSelectedAttributeValues,
  mergeChoicesWithValues,
} from "@dashboard/attributes/utils/data";
import { AttributeInput, VariantAttributeScope } from "@dashboard/components/Attributes";
import {
  ProductDetailsVariantFragment,
  ProductFragment,
  ProductMediaFragment,
  ProductTypeQuery,
  ProductVariantCreateDataQuery,
  ProductVariantFragment,
  SelectedVariantAttributeFragment,
  StockInput,
  VariantAttributeFragment,
} from "@dashboard/graphql";
import { FormsetAtomicData } from "@dashboard/hooks/useFormset";
import { maybe } from "@dashboard/misc";
import { mapEdgesToItems, mapMetadataItemToInput } from "@dashboard/utils/maps";
import { Option } from "@saleor/macaw-ui-next";
import moment from "moment";

import { ProductStockInput } from "../components/ProductStocks";
import { ProductUpdateFormData } from "../components/ProductUpdatePage/types";

export interface Collection {
  id: string;
  label: string;
}

interface Node {
  id: string;
  name: string;
}

export interface ProductType {
  hasVariants: boolean;
  id: string;
  name: string;
  productAttributes: ProductTypeQuery["productType"]["productAttributes"];
}

function buildSelectionIds(
  selectionAttrs: Array<{ id: string }> | null | undefined,
): Set<string> {
  return new Set(selectionAttrs?.map(a => a.id) ?? []);
}

export function getVariantAttributeInputFromGroupedAttributes(
  variantAttributesGrouped: Array<{
    parent?: { id: string; name: string; slug: string } | null;
    attributes: Array<{ attribute: VariantAttributeFragment }>;
  }>,
  selectionVariantAttributes: Array<{ id: string }> | null | undefined,
  nonSelectionVariantAttributes: Array<{ id: string }> | null | undefined,
): AttributeInput[] {
  const selectionIds = buildSelectionIds(selectionVariantAttributes);
  const nonSelectionIds = buildSelectionIds(nonSelectionVariantAttributes);

  const result: AttributeInput[] = [];

  variantAttributesGrouped?.forEach(group => {
    if (group.parent) {
      group.attributes.forEach(assignedAttr => {
        const id = assignedAttr.attribute.id;
        const variantAttributeScope = selectionIds.has(id)
          ? VariantAttributeScope.VARIANT_SELECTION
          : nonSelectionIds.has(id)
            ? VariantAttributeScope.NOT_VARIANT_SELECTION
            : VariantAttributeScope.NOT_VARIANT_SELECTION;

        result.push({
          data: {
            entityType: assignedAttr.attribute.entityType,
            inputType: assignedAttr.attribute.inputType,
            isRequired: assignedAttr.attribute.valueRequired,
            values: mapEdgesToItems(assignedAttr.attribute.choices) || [],
            unit: assignedAttr.attribute.unit,
            variantAttributeScope,
            parentAttribute: {
              id: group.parent!.id,
              name: group.parent!.name,
              slug: group.parent!.slug,
            },
            childLabel: assignedAttr.attribute.name,
          },
          id,
          label: `${group.parent!.name} - ${assignedAttr.attribute.name}`,
          value: [],
        });
      });
    } else {
      group.attributes.forEach(assignedAttr => {
        const id = assignedAttr.attribute.id;
        const variantAttributeScope = selectionIds.has(id)
          ? VariantAttributeScope.VARIANT_SELECTION
          : nonSelectionIds.has(id)
            ? VariantAttributeScope.NOT_VARIANT_SELECTION
            : VariantAttributeScope.NOT_VARIANT_SELECTION;

        result.push({
          data: {
            entityType: assignedAttr.attribute.entityType,
            inputType: assignedAttr.attribute.inputType,
            isRequired: assignedAttr.attribute.valueRequired,
            values: mapEdgesToItems(assignedAttr.attribute.choices) || [],
            unit: assignedAttr.attribute.unit,
            variantAttributeScope,
          },
          id,
          label: assignedAttr.attribute.name ?? "",
          value: [],
        });
      });
    }
  });

  return result;
}

export function getAttributeInputFromProduct(product: ProductFragment): AttributeInput[] {
  return (
    product?.attributes?.map(attribute => ({
      data: {
        entityType: attribute.attribute.entityType,
        inputType: attribute.attribute.inputType,
        isRequired: attribute.attribute.valueRequired,
        selectedValues: attribute.values,
        values: mergeChoicesWithValues(attribute),
        unit: attribute.attribute.unit,
      },
      id: attribute.attribute.id,
      label: attribute.attribute.name,
      value: getSelectedAttributeValues(attribute),
      metadata: getReferenceAttributeValuesLabels(attribute),
    })) ?? []
  );
}
export interface AttributeValuesMetadata {
  value: string;
  label: string;
}

const getReferenceAttributeValuesLabels = (
  attribute: ProductFragment["attributes"][0],
): AttributeValuesMetadata[] => {
  return attribute.values.map(value => {
    return {
      label: value.name,
      value: value.reference,
    };
  });
};

export function getAttributeInputFromProductType(productType: ProductType): AttributeInput[] {
  return productType.productAttributes.map(attribute => ({
    data: {
      entityType: attribute.entityType,
      inputType: attribute.inputType,
      isRequired: attribute.valueRequired,
      values: mapEdgesToItems(attribute.choices) || [],
      unit: attribute.unit,
    },
    id: attribute.id,
    label: attribute.name,
    value: [],
  }));
}

export function getAttributeInputFromAttributes(
  variantAttributes: VariantAttributeFragment[],
  variantAttributeScope: VariantAttributeScope,
): AttributeInput[] {
  return variantAttributes?.map(attribute => ({
    data: {
      entityType: attribute.entityType,
      inputType: attribute.inputType,
      isRequired: attribute.valueRequired,
      values: mapEdgesToItems(attribute.choices) || [],
      unit: attribute.unit,
      variantAttributeScope,
    },
    id: attribute.id,
    label: attribute.name,
    value: [],
  }));
}

export function getAttributeInputFromSelectedAttributes(
  variantAttributes: SelectedVariantAttributeFragment[],
  variantAttributeScope: VariantAttributeScope,
): AttributeInput[] {
  return variantAttributes?.map(attribute => ({
    data: {
      entityType: attribute.attribute.entityType,
      inputType: attribute.attribute.inputType,
      isRequired: attribute.attribute.valueRequired,
      selectedValues: attribute.values,
      values: mergeChoicesWithValues(attribute),
      unit: attribute.attribute.unit,
      variantAttributeScope,
    },
    id: attribute.attribute.id,
    label: attribute.attribute.name,
    value: getSelectedAttributeValues(attribute),
  }));
}

export function getAttributeInputFromVariant(variant: ProductVariantFragment): AttributeInput[] {
  const selectionAttributeInput = getAttributeInputFromSelectedAttributes(
    variant?.selectionAttributes,
    VariantAttributeScope.VARIANT_SELECTION,
  );
  const nonSelectionAttributeInput = getAttributeInputFromSelectedAttributes(
    variant?.nonSelectionAttributes,
    VariantAttributeScope.NOT_VARIANT_SELECTION,
  );

  return selectionAttributeInput?.concat(nonSelectionAttributeInput ?? []) ?? [];
}

/**
 * Enriches variant attributes with parent-child hierarchy info from variantAttributesGrouped.
 * This is used by the Existing Variant Screen to match the behavior of the New Variant Screen.
 */
export function getAttributeInputFromVariantWithGroupedInfo(
  variant: ProductVariantFragment,
  variantAttributesGrouped?: Array<{
    parent?: { id: string; name: string; slug: string } | null;
    attributes: Array<{ attribute: { id: string; name?: string | null; slug?: string | null } }>;
  }> | null,
): AttributeInput[] {
  // Get basic attribute input from variant
  const attributeInput = getAttributeInputFromVariant(variant);

  // If no grouped attributes, return as is
  if (!variantAttributesGrouped) {
    return attributeInput;
  }

  // Build a lookup map: attribute ID -> parent info
  const parentLookup = new Map<string, { id: string; name: string; slug: string }>();
  for (const group of variantAttributesGrouped) {
    if (group.parent) {
      for (const attr of group.attributes) {
        parentLookup.set(attr.attribute.id, {
          id: group.parent.id,
          name: group.parent.name,
          slug: group.parent.slug,
        });
      }
    }
  }

  // Enrich attributes with parent info
  return attributeInput.map(attr => {
    const parentAttribute = parentLookup.get(attr.id);
    if (parentAttribute) {
      return {
        ...attr,
        label: `${parentAttribute.name} - ${attr.label}`,
        data: {
          ...attr.data,
          parentAttribute,
          childLabel: attr.label,
        },
      };
    }
    return attr;
  });
}

export function getVariantAttributeInputFromProduct(
  product: ProductVariantCreateDataQuery["product"],
): AttributeInput[] {
  if (product?.productType?.variantAttributesGrouped) {
    return getVariantAttributeInputFromGroupedAttributes(
      product.productType.variantAttributesGrouped,
      product.productType.selectionVariantAttributes ?? null,
      product.productType.nonSelectionVariantAttributes ?? null,
    );
  }

  const selectionAttributeInput = getAttributeInputFromAttributes(
    product?.productType?.selectionVariantAttributes,
    VariantAttributeScope.VARIANT_SELECTION,
  );
  const nonSelectionAttributeInput = getAttributeInputFromAttributes(
    product?.productType?.nonSelectionVariantAttributes,
    VariantAttributeScope.NOT_VARIANT_SELECTION,
  );

  return selectionAttributeInput?.concat(nonSelectionAttributeInput ?? []) ?? [];
}

export function getStockInputFromVariant(variant: ProductVariantFragment): ProductStockInput[] {
  return (
    variant?.stocks.map(stock => ({
      data: {
        quantityAllocated: stock.quantityAllocated,
      },
      id: stock.warehouse.id,
      label: stock.warehouse.name,
      value: stock.quantity.toString(),
    })) || []
  );
}

export function getCollectionInput(
  productCollections: ProductFragment["collections"],
): Collection[] {
  return maybe(
    () =>
      productCollections.map(collection => ({
        id: collection.id,
        label: collection.name,
      })),
    [],
  );
}

export function getChoices(nodes: Node[]): Option[] {
  return maybe(
    () =>
      nodes.map(node => ({
        label: node.name,
        value: node.id,
      })),
    [],
  );
}

export function getProductUpdatePageFormData(
  product: ProductFragment,
  variants: ProductDetailsVariantFragment[],
): ProductUpdateFormData {
  const variant = product?.variants[0];

  return {
    category: maybe(() => product.category.id, ""),
    taxClassId: product?.taxClass?.id,
    collections: maybe(
      () =>
        product.collections.map(collection => ({
          label: collection.name,
          value: collection.id,
        })),
      [],
    ),
    isAvailable: !!product?.isAvailable,
    metadata: product?.metadata?.map(mapMetadataItemToInput),
    name: maybe(() => product.name, ""),
    privateMetadata: product?.privateMetadata?.map(mapMetadataItemToInput),
    rating: maybe(() => product.rating, null),
    seoDescription: maybe(() => product.seoDescription, ""),
    seoTitle: maybe(() => product.seoTitle, ""),
    sku: maybe(
      () =>
        product.productType.hasVariants
          ? undefined
          : variants && variants[0]
            ? variants[0].sku
            : undefined,
      "",
    ),
    slug: product?.slug || "",
    trackInventory: !!variant?.trackInventory,
    weight: product?.weight?.value.toString() || "",
    isPreorder: !!variant?.preorder || false,
    globalThreshold: variant?.preorder?.globalThreshold?.toString() || "",
    globalSoldUnits: variant?.preorder?.globalSoldUnits || 0,
    hasPreorderEndDate: !!variant?.preorder?.endDate,
    preorderEndDateTime: variant?.preorder?.endDate,
  };
}

export function mapFormsetStockToStockInput(stock: FormsetAtomicData<null, string>): StockInput {
  return {
    quantity: parseInt(stock.value, 10) || 0,
    warehouse: stock.id,
  };
}

export const getPreorderEndDateFormData = (endDate?: string) =>
  endDate ? moment(endDate).format("YYYY-MM-DD") : "";

export const getPreorderEndHourFormData = (endDate?: string) =>
  endDate ? moment(endDate).format("HH:mm") : "";

export const getSelectedMedia = <T extends Pick<ProductMediaFragment, "id" | "sortOrder">>(
  media: T[] = [],
  selectedMediaIds: string[],
) =>
  media
    .filter(image => selectedMediaIds.includes(image.id))
    .sort((prev, next) => (prev.sortOrder > next.sortOrder ? 1 : -1));
